import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Result, Button, Typography, Space, Tag } from 'antd';
import { BugOutlined, ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import { prepareErrorForDisplay, type ErrorDisplayInfo } from '../../services/core/ApiErrorHandler';
import { generateErrorId, reportErrorToService, addBreadcrumb } from '../../utils/errorReporting';
import { openSupportWithError } from '../../utils/openSupportWithError';
import { getPageLocation } from '../../utils/getPageLocation';
import { ErrorDetailsPanel } from './ErrorDetailsPanel';

const { Text, Paragraph } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  timestamp: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorId: '',
      timestamp: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = generateErrorId();
    const timestamp = Date.now();
    
    return { 
      hasError: true, 
      error,
      errorId,
      timestamp
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.group('🚨 ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Error ID:', this.state.errorId);
    console.groupEnd();

    this.setState({ errorInfo });

    this.props.onError?.(error, errorInfo);

    addBreadcrumb({
      type: 'user-action',
      timestamp: Date.now(),
      message: `React Error Boundary caught: ${error.message}`,
      data: { errorId: this.state.errorId },
    });

    if (import.meta.env.PROD) {
      reportErrorToService({
        errorId: this.state.errorId,
        timestamp: this.state.timestamp,
        error,
        errorInfo,
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorId: '',
      timestamp: 0
    });
  };

  handleReportProblem = () => {
    openSupportWithError({
      location: getPageLocation(),
      errorMessage: this.state.error?.message ?? 'Неизвестная ошибка',
      errorId: this.state.errorId,
      stack: this.state.error?.stack,
    });
  };

  private renderErrorDetails = (displayInfo: ErrorDisplayInfo) => (
    <ErrorDetailsPanel
      displayInfo={displayInfo}
      error={this.state.error}
      errorInfo={this.state.errorInfo}
      errorId={this.state.errorId}
    />
  );

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const displayInfo = prepareErrorForDisplay(this.state.error);

      return (
        <Result
          status={displayInfo.severity === 'warning' ? 'warning' : 'error'}
          title={
            <Space>
              {displayInfo.title}
              <Tag color={displayInfo.severity === 'error' ? 'red' : 'orange'}>
                {displayInfo.severity.toUpperCase()}
              </Tag>
            </Space>
          }
          subTitle={
            <div style={{ maxWidth: 600, margin: '0 auto' }}>
              <Paragraph>{displayInfo.message}</Paragraph>
              {displayInfo.operationId && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">ID операции: </Text>
                  <Text code>{displayInfo.operationId}</Text>
                </div>
              )}
            </div>
          }
          extra={[
            displayInfo.canRetry && (
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={this.handleReset} 
                key="retry"
              >
                Попробовать снова
              </Button>
            ),
            
            <Button 
              icon={<HomeOutlined />}
              onClick={() => window.location.reload()} 
              key="reload"
            >
              Обновить страницу
            </Button>,
            
            displayInfo.canReport && (
              <Button 
                icon={<BugOutlined />}
                onClick={this.handleReportProblem} 
                key="report"
                type="default"
              >
                Сообщить о проблеме
              </Button>
            ),
          ].filter(Boolean)}
        >
          {(this.props.showDetails || import.meta.env.DEV) && 
            this.renderErrorDetails(displayInfo)
          }
        </Result>
      );
    }

    return this.props.children;
  }
}
